import { useMutation, useQuery, useQueryClient } from 'react-query'
import { QueryFunctionContext } from 'react-query/types/core/types'
import produce from 'immer'
import { DateTime } from 'luxon'
import { TGoalReview } from '../../components/goals/goalReviewRules'
import {
    TGoal,
    TGoalContribution,
    TGoalPatch,
    TGoalReviewEntry,
    TGoalTaskHandling,
    normalizeGoalEndDate,
} from '../../components/goals/goalTypes'
import { msUntilNextLocalDay } from '../../components/goals/weekHistoryRules'
import apiClient from '../../utils/api'
import {
    OptimisticTransaction,
    beginOptimisticTransaction,
    optimisticQueryChange,
    reconcileOptimisticTransaction,
    rollbackOptimisticTransaction,
} from '../optimisticTransaction'
import { GTQueryClient, useGTMutation, useGTQueryClient } from '../queryUtils'
import { useIsFeatureEnabled } from './feature-flags.hooks'

export type TGoalTaskLinks = Record<string, string>
export type TGoalRecurringTemplateLinks = Record<string, string[]>

/**
 * Every derived field of a goal (the week rows, this week's count, the verdict) is counted for the
 * local day the server read it, and queries here never go stale on their own (staleTime: Infinity).
 * So the list is read again just after local midnight: without that, a tab left open past Sunday
 * keeps showing last week as this week until some unrelated edit happens to invalidate it. Every
 * mounted useGetGoals schedules the same moment, and react-query folds those into one request.
 */
export const useGetGoals = () =>
    useQuery<TGoal[], void>('goals', getGoals, {
        enabled: useIsFeatureEnabled('goals'),
        refetchInterval: () => msUntilNextLocalDay(),
    })

const getGoals = async ({ signal }: QueryFunctionContext) => {
    const res = await apiClient.get('/goals/', { signal })
    return res.data as TGoal[]
}

export const useGetGoalTaskLinks = () =>
    useQuery<TGoalTaskLinks, void>('goal-task-links', getGoalTaskLinks, { enabled: useIsFeatureEnabled('goals') })

const getGoalTaskLinks = async ({ signal }: QueryFunctionContext) => {
    const res = await apiClient.get('/goals/task_links/', { signal })
    return res.data as TGoalTaskLinks
}

export const useGetGoalRecurringTemplateLinks = () =>
    useQuery<TGoalRecurringTemplateLinks, void>('goal-recurring-template-links', getGoalRecurringTemplateLinks, {
        enabled: useIsFeatureEnabled('goals'),
    })

const getGoalRecurringTemplateLinks = async ({ signal }: QueryFunctionContext) => {
    const res = await apiClient.get('/goals/recurring_template_links/', { signal })
    return res.data as TGoalRecurringTemplateLinks
}

export const useCreateGoal = () => {
    const queryClient = useGTQueryClient()
    return useMutation<TGoal, unknown, TGoal, OptimisticTransaction<TGoal>>((goal) => createGoal(goal), {
        onMutate: (goal) => {
            void queryClient.cancelQueries('goals')
            return beginOptimisticTransaction(queryClient, [
                optimisticQueryChange<TGoal[], TGoal>(
                    'goals',
                    (current) => [...(current ?? []), { ...goal, optimisticId: goal.id }],
                    {
                        rollback: (current) => current?.filter((candidate) => candidate.id !== goal.id),
                        reconcile: (current, saved) =>
                            current?.map((candidate) => (candidate.id === goal.id ? saved : candidate)),
                    }
                ),
            ])
        },
        onSuccess: (saved, _goal, transaction) => reconcileOptimisticTransaction(transaction, saved),
        onError: (_error, _goal, transaction) => rollbackOptimisticTransaction(transaction),
        onSettled: () => queryClient.invalidateQueries('goals'),
    })
}

const createGoal = async (goal: TGoal) => {
    const res = await apiClient.post('/goals/', goal)
    return res.data as TGoal
}

/**
 * Only the fields the client owns. Progress, status labels, the week cells and the contract line
 * are recomputed server-side on every write, so they are left alone here and arrive with the
 * refetch — showing a guess at a goal's verdict would be worse than showing it a moment late.
 */
const optimisticallyUpdateGoal = (queryClient: GTQueryClient, goalId: string, patch: TGoalPatch) => {
    const goals = queryClient.getImmutableQueryData<TGoal[]>('goals')
    if (!goals) return
    queryClient.setQueryData<TGoal[]>(
        'goals',
        produce(goals, (draft) => {
            const goal = draft.find((g) => g.id === goalId)
            if (!goal) return
            if (patch.title !== undefined) goal.title = patch.title
            if (patch.why !== undefined) goal.why = patch.why
            if (patch.timeframeLabel !== undefined) goal.timeframeLabel = patch.timeframeLabel
            if (patch.targetLabel !== undefined) goal.targetLabel = patch.targetLabel
            if (patch.paceLabel !== undefined) goal.paceLabel = patch.paceLabel
            if (patch.progressMode !== undefined) goal.progressMode = patch.progressMode
            if (patch.goalType !== undefined) goal.goalType = patch.goalType
            if (patch.phases !== undefined) goal.phases = patch.phases
            if (patch.planCadences !== undefined) goal.planCadences = patch.planCadences
            if (patch.status !== undefined) goal.status = patch.status
            if (patch.startDate !== undefined) goal.startDate = patch.startDate
            if (patch.endDate !== undefined) goal.endDate = normalizeGoalEndDate(patch.endDate)
            if (patch.weeklyTarget !== undefined) goal.weeklyTarget = patch.weeklyTarget
            if (patch.rampStepUpDisabled !== undefined) goal.rampStepUpDisabled = patch.rampStepUpDisabled
            if (patch.unit !== undefined) {
                if (patch.unit) goal.unit = patch.unit
                else delete goal.unit
            }
        }) as TGoal[]
    )
}

/**
 * The other half: a PATCH answers with the goal the server just recomputed, so the derived fields
 * are taken straight from it rather than waiting on the refetch. The client-owned fields are
 * deliberately not touched — a response can land while the user is still typing, and writing its
 * older title back would eat every keystroke since.
 *
 * This is also what keeps a burst of edits honest. useGTMutation only records a queued request as
 * "last sent" when it starts a queue, so when a second edit is queued behind a first, the second
 * one's settle path sees a stale marker and skips its invalidate. Reconciling here means the
 * verdict, the week cells and the contract line are right after every write regardless.
 */
const reconcileDerivedGoalFields = (queryClient: GTQueryClient, updated: TGoal) => {
    const goals = queryClient.getImmutableQueryData<TGoal[]>('goals')
    if (!goals || !updated?.id) return
    queryClient.setQueryData<TGoal[]>(
        'goals',
        produce(goals, (draft) => {
            const goal = draft.find((g) => g.id === updated.id)
            if (!goal) return
            goal.progress = updated.progress
            goal.progressLabel = updated.progressLabel
            goal.status = updated.status
            goal.weekLabel = updated.weekLabel
            goal.weeks = updated.weeks
            goal.contractLine = updated.contractLine
            goal.thisWeek = updated.thisWeek
            goal.asOf = updated.asOf
        }) as TGoal[]
    )
}

/**
 * Queued like task edits: goal text saves as you type, so a plain invalidate-on-success would land
 * a refetch mid-keystroke and write the server's older title back into the field. useGTMutation
 * serializes the writes and holds the refetch until typing stops.
 */
export const useUpdateGoal = () => {
    const queryClient = useGTQueryClient()
    return useGTMutation(({ goalId, patch }: { goalId: string; patch: TGoalPatch }) => updateGoal(goalId, patch), {
        tag: 'goals',
        invalidateTagsOnSettled: ['goals'],
        errorMessage: 'edit goal',
        onMutate: async ({ goalId, patch }: { goalId: string; patch: TGoalPatch }) => {
            await queryClient.cancelQueries('goals')
            optimisticallyUpdateGoal(queryClient, goalId, patch)
        },
        onSuccess: (updated: TGoal) => reconcileDerivedGoalFields(queryClient, updated),
    })
}

const updateGoal = async (goalId: string, patch: TGoalPatch) => {
    const body = patch.endDate === undefined ? patch : { ...patch, endDate: normalizeGoalEndDate(patch.endDate) }
    const res = await apiClient.patch(`/goals/${goalId}/`, body)
    return res.data as TGoal
}

/**
 * Archiving is one server call because it is several writes that have to agree: the goal's tasks are
 * handled, its recurring hooks are dropped, its contributions are discarded and archived_at is
 * stamped. Nothing is applied optimistically — a row that vanishes and then comes back because the
 * write failed is worse than a row that leaves a moment late, and this is the one action the user
 * cannot undo the history of.
 *
 * The invalidation is wide because "archive the tasks too" removes tasks: every list that draws
 * tasks has to hear about it, not just the goals list.
 */
const ARCHIVE_INVALIDATE_TAGS = [
    'goals',
    'goal-task-links',
    'goal-recurring-template-links',
    'tasks_v4',
    'folders',
    'overview',
] as const

interface GoalLifecycleContext {
    transaction?: OptimisticTransaction<TGoal>
}

const optimisticGoalLifecycle = (
    queryClient: GTQueryClient,
    goalId: string,
    action: 'reopen' | 'unarchive'
): GoalLifecycleContext => {
    void queryClient.cancelQueries('goals')
    return {
        transaction: beginOptimisticTransaction(queryClient, [
            optimisticQueryChange<TGoal[], TGoal>(
                'goals',
                (current) =>
                    current
                        ? produce(current, (draft) => {
                              const goal = draft.find((candidate) => candidate.id === goalId)
                              if (!goal) return
                              if (action === 'reopen') delete goal.achievedAt
                              else {
                                  delete goal.archivedAt
                                  delete goal.archivedReason
                              }
                          })
                        : current,
                {
                    rollback: (current, previous) => {
                        if (!current || !previous) return current
                        const oldGoal = previous.find((candidate) => candidate.id === goalId)
                        if (!oldGoal) return current
                        return produce(current, (draft) => {
                            const goal = draft.find((candidate) => candidate.id === goalId)
                            if (!goal) return
                            if (action === 'reopen' && goal.achievedAt === undefined)
                                goal.achievedAt = oldGoal.achievedAt
                            if (action === 'unarchive' && goal.archivedAt === undefined) {
                                goal.archivedAt = oldGoal.archivedAt
                                goal.archivedReason = oldGoal.archivedReason
                            }
                        })
                    },
                    // A queued edit may already have changed client-owned fields while this request was in flight.
                    // Settle only the lifecycle fields this action owns; derived fields reconcile on success below.
                    reconcile: (current, updated) =>
                        current
                            ? produce(current, (draft) => {
                                  const goal = draft.find((candidate) => candidate.id === goalId)
                                  if (!goal) return
                                  if (action === 'reopen') {
                                      if (updated.achievedAt === undefined) delete goal.achievedAt
                                      else goal.achievedAt = updated.achievedAt
                                  } else {
                                      if (updated.archivedAt === undefined) delete goal.archivedAt
                                      else goal.archivedAt = updated.archivedAt
                                      if (updated.archivedReason === undefined) delete goal.archivedReason
                                      else goal.archivedReason = updated.archivedReason
                                  }
                              })
                            : current,
                }
            ),
        ]),
    }
}

export const useArchiveGoal = () => {
    return useGTMutation(
        ({ goalId, taskHandling, reason }: { goalId: string; taskHandling: TGoalTaskHandling; reason?: 'done' }) =>
            archiveGoal(goalId, taskHandling, reason),
        {
            tag: 'goals',
            invalidateTagsOnSettled: [...ARCHIVE_INVALIDATE_TAGS],
            errorMessage: 'archive goal',
        }
    )
}

const archiveGoal = async (goalId: string, taskHandling: TGoalTaskHandling, reason?: 'done') => {
    const res = await apiClient.post(`/goals/${goalId}/archive/`, { task_handling: taskHandling, reason })
    return res.data as TGoal
}

export const useUnarchiveGoal = () => {
    const queryClient = useGTQueryClient()
    return useGTMutation<TGoal, unknown, string, GoalLifecycleContext>((goalId) => unarchiveGoal(goalId), {
        tag: 'goals',
        invalidateTagsOnSettled: [...ARCHIVE_INVALIDATE_TAGS],
        errorMessage: 'restore goal',
        onMutate: (goalId) => optimisticGoalLifecycle(queryClient, goalId, 'unarchive'),
        onSuccess: (updated, _goalId, context) => {
            reconcileOptimisticTransaction(context?.transaction, updated)
            reconcileDerivedGoalFields(queryClient, updated)
        },
        onError: (_error, _goalId, context) => rollbackOptimisticTransaction(context?.transaction),
    })
}

const unarchiveGoal = async (goalId: string) => {
    const res = await apiClient.post(`/goals/${goalId}/unarchive/`)
    return res.data as TGoal
}

/**
 * Achieving is one server call for the same reason archiving is: open tasks are handled, hooks are
 * dropped and achieved_at is stamped last. Not optimistic either — the celebration should only fire
 * for a goal that really moved, and a failure leaves the confirm open to retry. Its history is kept,
 * so unlike archive there is nothing here the user cannot get back by reopening.
 */
export const useAchieveGoal = () => {
    return useGTMutation(
        ({ goalId, taskHandling }: { goalId: string; taskHandling: TGoalTaskHandling }) =>
            achieveGoal(goalId, taskHandling),
        {
            tag: 'goals',
            invalidateTagsOnSettled: [...ARCHIVE_INVALIDATE_TAGS],
            errorMessage: 'mark goal achieved',
        }
    )
}

const achieveGoal = async (goalId: string, taskHandling: TGoalTaskHandling) => {
    const res = await apiClient.post(`/goals/${goalId}/achieve/`, { task_handling: taskHandling })
    return res.data as TGoal
}

export const useReopenGoal = () => {
    const queryClient = useGTQueryClient()
    return useGTMutation<TGoal, unknown, string, GoalLifecycleContext>((goalId: string) => reopenGoal(goalId), {
        tag: 'goals',
        invalidateTagsOnSettled: [...ARCHIVE_INVALIDATE_TAGS],
        errorMessage: 'reopen goal',
        onMutate: (goalId) => optimisticGoalLifecycle(queryClient, goalId, 'reopen'),
        onSuccess: (updated, _goalId, context) => {
            reconcileOptimisticTransaction(context?.transaction, updated)
            reconcileDerivedGoalFields(queryClient, updated)
        },
        onError: (_error, _goalId, context) => rollbackOptimisticTransaction(context?.transaction),
    })
}

const reopenGoal = async (goalId: string) => {
    const res = await apiClient.post(`/goals/${goalId}/reopen/`)
    return res.data as TGoal
}

/** This review week's answers. Read by the Overview card, the Goals page and the review pane. */
export const useGetGoalReview = () =>
    useQuery<TGoalReview, void>('goal-review', getGoalReview, { enabled: useIsFeatureEnabled('goals') })

const getGoalReview = async ({ signal }: QueryFunctionContext) => {
    const res = await apiClient.get('/goals/reviews/current/', { signal })
    return res.data as TGoalReview
}

/** What the client sends for one goal; the server stamps the time and snapshots the step's title. */
export type TGoalReviewAnswer = Pick<TGoalReviewEntry, 'read' | 'fix' | 'stepTaskId' | 'stepDay' | 'skipped'>

/**
 * Nothing is applied optimistically. An answer moves the queue on, and a queue that advanced past a
 * goal whose answer then failed to save would show a finished review that is not one. The response
 * is the whole week, so it replaces the cache directly; goals refetch because each one carries its
 * own reads.
 */
export const useSaveGoalReviewEntry = () => {
    const queryClient = useQueryClient()
    return useMutation(
        ({ goalId, answer }: { goalId: string; answer: TGoalReviewAnswer }) => saveGoalReviewEntry(goalId, answer),
        {
            onSuccess: (review) => {
                queryClient.setQueryData('goal-review', review)
                queryClient.invalidateQueries('goals')
            },
        }
    )
}

const saveGoalReviewEntry = async (goalId: string, answer: TGoalReviewAnswer) => {
    const res = await apiClient.patch(`/goals/reviews/current/goals/${goalId}/`, answer)
    return res.data as TGoalReview
}

/** "Not this week" on the Overview card. Per week: the card returns with the next review. */
export const useDismissGoalReview = () => {
    const queryClient = useGTQueryClient()
    return useMutation<TGoalReview, unknown, void, OptimisticTransaction<TGoalReview>>(() => dismissGoalReview(), {
        onMutate: () => {
            void queryClient.cancelQueries('goal-review')
            return beginOptimisticTransaction(queryClient, [
                optimisticQueryChange<TGoalReview, TGoalReview>(
                    'goal-review',
                    (current) => (current ? { ...current, dismissed: true } : current),
                    { reconcile: (_current, review) => review }
                ),
            ])
        },
        onSuccess: (review, _variables, transaction) => reconcileOptimisticTransaction(transaction, review),
        onError: (_error, _variables, transaction) => rollbackOptimisticTransaction(transaction),
    })
}

const dismissGoalReview = async () => {
    const res = await apiClient.patch('/goals/reviews/current/', { dismissed: true })
    return res.data as TGoalReview
}

export interface TGoalLog {
    goalId: string
    /** Client-generated key: a retried request is saved once. */
    id: string
    title: string
    /** In the goal's unit; 0 records the log without counting toward the week. */
    amount: number
    note?: string
}

/**
 * Adding one by hand arms in place and commits under the cursor, so the unit has to appear at once —
 * waiting a refetch for the meter to move reads as a click that did nothing. The optimistic entry
 * is appended to `recent` *and* to `thisWeekUnits`, because the meter draws its glyphs from the
 * latter; both are replaced wholesale by the server's own answer on success. A note (amount 0)
 * still lands in `recent` but never in the units, matching what recomputeGoal will decide.
 */
export const useLogGoalProgress = () => {
    const queryClient = useGTQueryClient()
    return useMutation((log: TGoalLog) => logGoalProgress(log), {
        onMutate: async ({ goalId, id, title, amount, note }: TGoalLog) => {
            await queryClient.cancelQueries('goals')
            const goals = queryClient.getImmutableQueryData<TGoal[]>('goals')
            if (!goals) return
            const pending: TGoalContribution = {
                id: `log-${id}`,
                title,
                source: 'Logged',
                at: DateTime.local().toISO(),
                amount,
                note,
            }
            queryClient.setQueryData<TGoal[]>(
                'goals',
                produce(goals, (draft) => {
                    const goal = draft.find((g) => g.id === goalId)
                    if (!goal) return
                    goal.recent = [pending, ...goal.recent]
                    if (amount <= 0) return
                    goal.thisWeek = (goal.thisWeek ?? 0) + amount
                    goal.thisWeekUnits = [...(goal.thisWeekUnits ?? []), pending]
                }) as TGoal[]
            )
            return goals
        },
        // What this guess fakes is progress, so a failed write has to take it straight back rather
        // than leave a unit on the meter until a refetch happens to correct it — the refetch may
        // itself fail, and a meter that claims work you did not do is the defect this block exists
        // to prevent. The invalidate still runs afterwards and the server's answer wins regardless.
        onError: (_error, _log, rollback) => {
            if (rollback) queryClient.setQueryData<TGoal[]>('goals', rollback as TGoal[])
        },
        onSettled: () => queryClient.invalidateQueries('goals'),
    })
}

const logGoalProgress = async ({ goalId, ...log }: TGoalLog) => {
    const res = await apiClient.post(`/goals/${goalId}/recent/`, log)
    return res.data as TGoal
}

/**
 * Queued with task mutations so linking a just-created task waits for its real id.
 * Goals refetch afterwards: moving a done task between goals moves its contribution.
 */
export const useLinkTaskToGoal = () => {
    const queryClient = useGTQueryClient()
    return useGTMutation<void, unknown, { id: string; goalId: string | null }, { id: string; previousGoalId?: string }>(
        ({ id, goalId }) => {
            // A link queued behind its task's creation is sent under the real id, but onMutate wrote
            // it under the optimistic one — which useCreateTask has just cleared from the task. Mirror
            // it under the id being sent so the task stays in its goal while the request is out.
            if (goalId) {
                queryClient.setQueryData<TGoalTaskLinks>('goal-task-links', (current) => ({
                    ...(current ?? {}),
                    [id]: goalId,
                }))
            }
            return linkTaskToGoal(id, goalId)
        },
        {
            tag: 'tasks_v4',
            invalidateTagsOnSettled: ['goal-task-links', 'goals', 'tasks_v4', 'folders', 'overview'],
            errorMessage: 'change goal',
            /**
             * The optimistic write below is keyed by whatever id the caller had, which for a task
             * created and linked in one go is its optimistic id — and useCreateTask clears that id
             * as soon as the server answers, so goalIdForTask can no longer find the link and the
             * task drops out of its goal. invalidateTagsOnSettled does not rescue it: useGTMutation
             * skips that path unless this request was the last one sent, which a queued optimistic
             * link never is. This hook runs on every settle, so the map is refetched under real ids.
             */
            onSettled: () => queryClient.invalidateQueries('goal-task-links'),
            onError: (_error, variables, rollback) => {
                if (!rollback) return
                queryClient.setQueryData<TGoalTaskLinks>('goal-task-links', (current) => {
                    const next = { ...(current ?? {}) }
                    // Sent under a real id the optimistic write never knew: drop the mirrored entry too.
                    if (variables.id !== rollback.id) delete next[variables.id]
                    if (rollback.previousGoalId) next[rollback.id] = rollback.previousGoalId
                    else delete next[rollback.id]
                    return next
                })
            },
            onMutate: ({ id, goalId }) => {
                queryClient.cancelQueries('goal-task-links')
                const previousGoalId = queryClient.getImmutableQueryData<TGoalTaskLinks>('goal-task-links')?.[id]
                queryClient.setQueryData<TGoalTaskLinks>('goal-task-links', (current) => {
                    const next = { ...(current ?? {}) }
                    if (goalId) next[id] = goalId
                    else delete next[id]
                    return next
                })
                return { id, previousGoalId }
            },
        }
    )
}

const linkTaskToGoal = async (taskId: string, goalId: string | null) => {
    const res = await apiClient.patch(`/goals/task_links/${taskId}/`, { goal_id: goalId })
    return res.data
}

export const useLinkRecurringTemplateToGoals = () => {
    const queryClient = useQueryClient()
    return useGTMutation(
        ({ id, goalIds }: { id: string; goalIds: string[] }) => linkRecurringTemplateToGoals(id, goalIds),
        {
            tag: 'recurring-tasks',
            invalidateTagsOnSettled: ['goal-recurring-template-links', 'goals', 'recurring-tasks'],
            errorMessage: 'change recurring goal hooks',
            onMutate: ({ id, goalIds }) => {
                queryClient.cancelQueries('goal-recurring-template-links')
                queryClient.setQueryData<TGoalRecurringTemplateLinks>('goal-recurring-template-links', (current) => ({
                    ...(current ?? {}),
                    [id]: goalIds,
                }))
            },
        }
    )
}

const linkRecurringTemplateToGoals = async (templateId: string, goalIds: string[]) => {
    const res = await apiClient.patch(`/goals/recurring_template_links/${templateId}/`, { goal_ids: goalIds })
    return res.data
}
